// utils/generatePrescriptionImage.js
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import moment from "moment";

export const GenerateMultipleVaccinationImage = async ({
  data,
  templateHtmlId = "hidden-vaccination-html",
}) => {
  // Create a hidden div
  const container = document.createElement("div");
  container.style.position = "absolute";
  container.style.top = "-9999px";
  container.style.left = "-9999px";
  container.style.margin = "20px";
  container.style.padding = "20px";
  container.style.borderRadius = "15px";
  container.id = templateHtmlId;
  container.innerHTML = generateVaccinationHTML(data); // use template data to render HTML
  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
    });

    const imageData = canvas.toDataURL("image/png");

    // Optional PDF logic
    const pdf = new jsPDF("p", "mm", "a4");
    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imageData, "PNG", 0, 0, imgWidth, imgHeight);
    // pdf.save("Prescription.pdf");

    // Clean up
    document.body.removeChild(container);

    return {
      base64Image: imageData,
      expiryDate: moment().add(6, "months").format("YYYY-MM-DD"),
    };
  } catch (error) {
    console.error("Failed to generate image from vaccination template:", error);
    document.body.removeChild(container);
    return null;
  }
};

const generateVaccinationHTML = (data) => {
  return `
<!DOCTYPE html>
      <html>
        <head>
          <title>Vaccination Certificate</title>
          <style>
            #hidden-vaccination-html {
              background-color: transparent !important;
              font-family: Arial, sans-serif;
              margin: 40px;
              line-height: 1.5;
              color: #333;
            }
            #hidden-vaccination-html .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #0066cc;
              padding-bottom: 20px;
            }
            #hidden-vaccination-html .pharmacy-name {
              font-size: 24px;
              font-weight: bold;
              color: #0066cc;
              margin-bottom: 10px;
            }
            #hidden-vaccination-html .pharmacy-info {
              font-size: 14px;
              margin-bottom: 5px;
            }
            #hidden-vaccination-html .certificate-title {
              font-size: 20px;
              font-weight: bold;
              text-align: center;
              margin: 30px 0;
              text-decoration: underline;
            }
            #hidden-vaccination-html .patient-info {
              margin-bottom: 30px;
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              text-align: left;
              gap: 10px;
            }
            #hidden-vaccination-html .patient-row {
              display: flex;
              justify-content: space-between;
              width: 100%;
            }
            #hidden-vaccination-html .patient-field {
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              margin-bottom: 8px;
            }
            #hidden-vaccination-html .patient-field .patient-value {
              padding-left: 9px;
              line-height: 0.8;
            }
            #hidden-vaccination-html .patient-field-address {
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              width: 100%;
              margin-bottom: 8px;
            }
            #hidden-vaccination-html .patient-label {
              font-weight: bold;
              padding: 4px 8px;
              border-radius: 4px;
              min-width: 60px;
            }
            #hidden-vaccination-html .patient-value {
              font-weight: normal;
              line-height: 0.8;
            }
            #hidden-vaccination-html .patient-value-address {
              font-weight: normal;
              padding-left: 9px;
              line-height: 0.8;
            }
            #hidden-vaccination-html .info-section {
              margin-bottom: 20px;
              }
            #hidden-vaccination-html .info-section p {
              padding-left: 19px;
            }
            #hidden-vaccination-html .table {
              width: 100%;
              border-collapse: collapse;
              margin: 15px 0;
            }
            #hidden-vaccination-html .table th, #hidden-vaccination-html .table td {
              border: 1px solid #333;
              padding: 8px;
              text-align: left;
            }
            #hidden-vaccination-html .table th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            #hidden-vaccination-html .table td:nth-child(2) {
              overflow-wrap: anywhere;
              word-break: break-word;
            }
            #hidden-vaccination-html .footer {
              background-color: transparent !important;
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #ccc;
              display: flex;
              flex-direction: column;
              gap: 4px;
            }
            #hidden-vaccination-html p {
              color: #000 !important;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="pharmacy-name">${
              data?.pharmacyDetails.teamPharmacyName
            }</div>
            <div class="pharmacy-info">${
              data.pharmacyDetails.teamPharmacyAddress || "-"
            }</div>
            <div class="pharmacy-info">Tel: ${
              data.pharmacyContactDetails.phoneNumber || "-"
            }</div>
          </div>

          <div class="certificate-title">Vaccination Certificate</div>

          <div class="patient-info">
            <div class="patient-row">
              <div class="patient-field">
                <div class="patient-label">Name</div>
                <div class="patient-value">${data?.patientDetails?.name || "-"}</div>
              </div>
              <div class="patient-field">
                <div class="patient-label">DOB</div>
                <div class="patient-value">${data?.patientDetails?.dob || "-"}</div>
              </div>
            </div>
            <div class="patient-field-address">
              <div class="patient-label">Address</div>
              <div class="patient-value-address">${data?.patientDetails?.address || "-"}</div>
            </div>
          </div>

          <div class="patient-field-address info-section">
            <div class="patient-value-address">
              <p>This certificate certifies that the above patient has had the following vaccinations:</p>
            </div>
          </div>

          <table class="table">
            <thead>
              <tr>
                <th>Vaccination</th>
                <th>Brand</th>
                <th>Vaccine site</th>
                <th>Batch #</th>
                <th>Route</th>
                <th>Date given</th>
                <th>Expiry date</th>
              </tr>
            </thead>
            <tbody>
                ${
                  (data?.vaccinationInfo || [])
                    .map(
                      (item, index) => `
                    <tr key=${index}>
                        <td>${item?.vaccineName ?? "-"}</td>
                        <td>${item?.manufacturer ?? "-"}</td>
                        <td>${item?.site ?? "-"}</td>
                        <td>${item?.batchNumber ?? "-"}</td>
                        <td>${item?.route ?? "-"}</td>
                        <td>${item?.administeredDate ?? "-"}</td>
                        <td>${item?.expiryDate ?? "-"}</td>
                    </tr>`,
                    )
                    .join("") ||
                  `
                    <tr>
                        <td colspan="6" style="text-align:center; padding:12px;">No Prescription Info Found</td>
                    </tr>`
                }
            </tbody>
          </table>

          <div class="patient-info">
            <div class="patient-row">
              <div class="patient-field">
                <div class="patient-label">Vaccination administered by</div>
                <div class="patient-value">${data?.pharmacistDetails.fullName || "-"}</div>
              </div>
              <div class="patient-field">
                <div class="patient-label">GPhC Number</div>
                <div class="patient-value">${data?.pharmacistDetails.gphcNumber || "-"}</div>
              </div>
            </div>
          </div>
        </body>
      </html>
`;
};
